import type {
  FileResponse,
  GetListResponseOfTaskAssignmentResponse,
  GetListResponseOfTaskDefinitionSimpleResponse,
  GetTaskListRequest,
  GetTaskListRequestV2,
  GetTasksSupervisorApprovalListRequest,
  TaskDefinitionRequest,
  TaskDefinitionResponse,
} from 'src/api/ProactApiClient';
import {taskDefinitionApiClient} from 'src/api/ProactApiClientInstances';
import {createActionMutationFn} from 'src/app/tasks/offline/mutations';
import type {OfflineActionCreateRequest} from 'src/app/tasks/offline/queuedTransition';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

export const taskDefinitionApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getTaskDefinitionList: builder.query<
      GetListResponseOfTaskDefinitionSimpleResponse,
      void
    >({
      queryFn: clientQuery(() =>
        taskDefinitionApiClient.list({
          length: 10000,
          start: 0,
          includeDemonstrationGenerated: false,
        })
      ),
      providesTags: ['TaskDefinitions'],
    }),
    getTaskDefinitionListByRequest: builder.query<
      GetListResponseOfTaskDefinitionSimpleResponse,
      GetTaskListRequest
    >({
      queryFn: clientQuery(params => taskDefinitionApiClient.list(params)),
      providesTags: ['TaskDefinitions'],
    }),
    getTaskDefinitionListV2: builder.query<
      GetListResponseOfTaskDefinitionSimpleResponse,
      GetTaskListRequestV2
    >({
      queryFn: clientQuery(request => taskDefinitionApiClient.listV2(request)),
      providesTags: ['TaskDefinitions'],
    }),
    getTaskDefinitionDetails: builder.query<TaskDefinitionResponse, number>({
      queryFn: clientQuery(taskId =>
        taskDefinitionApiClient.getDetails(taskId)
      ),
      providesTags: ['TaskDefinitions'],
    }),
    getTasksSupervisorApprovalList: builder.query<
      GetListResponseOfTaskAssignmentResponse,
      GetTasksSupervisorApprovalListRequest
    >({
      queryFn: clientQuery(request =>
        taskDefinitionApiClient.getTasksSupervisorApprovalList(request)
      ),
      providesTags: ['TaskDefinitions'],
    }),
    exportTaskStatusReport: builder.mutation<FileResponse | null, number>({
      queryFn: clientQuery(itemId =>
        taskDefinitionApiClient.exportTaskStatusReport(itemId)
      ),
    }),
    createTaskDefinition: builder.mutation<
      TaskDefinitionResponse,
      OfflineActionCreateRequest
    >({
      queryFn: createActionMutationFn,
      invalidatesTags: ['TaskDefinitions', 'TaskAssignments'],
    }),
    updateTaskDefinition: builder.mutation<
      TaskDefinitionResponse,
      TaskDefinitionRequest
    >({
      queryFn: clientQuery(model => taskDefinitionApiClient.update(model)),
      invalidatesTags: ['TaskDefinitions'],
    }),
    deleteTaskDefinition: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => taskDefinitionApiClient.delete(id)),
      invalidatesTags: ['TaskDefinitions'],
    }),
  }),
});

export const {
  useGetTaskDefinitionListQuery,
  useGetTaskDefinitionListByRequestQuery,
  useLazyGetTaskDefinitionListByRequestQuery,
  useGetTaskDefinitionListV2Query,
  useGetTaskDefinitionDetailsQuery,
  useLazyGetTaskDefinitionDetailsQuery,
  useGetTasksSupervisorApprovalListQuery,
  useExportTaskStatusReportMutation,
  useCreateTaskDefinitionMutation,
  useUpdateTaskDefinitionMutation,
  useDeleteTaskDefinitionMutation,
} = taskDefinitionApi;
